/**
 * SMS Customer View Flow
 *
 * This component handles SMS-based queue registration:
 * 1. Customer enters phone number
 * 2. System validates phone and checks CRM integration
 * 3. Customer selects services
 * 4. Queue is created and SMS is sent with queue number and link
 */
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    useTheme,
    useMediaQuery,
    Link as MuiLink,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import FMBlogo from "../../../assests/FMBlogo.png";
import {
    validatePhoneNumber,
    createSmsQueue,
    getAllServices,
} from "./../../../api/api";
import ScrollButtons from "../../../components/ScrollButtons";
import { getCurrentBranchId } from "../../../utils/auth";
import { getFrontendUrl } from "../../../config/config";

const SmsSelectionPage = () => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [queueNumber, setQueueNumber] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [smsSent, setSmsSent] = useState(false);
    const [smsContent, setSmsContent] = useState("");
    const [trackingUrl, setTrackingUrl] = useState("");
    const [step, setStep] = useState(1); // 1: Phone input, 2: Service selection, 3: Success
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const branchId = getCurrentBranchId();

    useEffect(() => {
        // Load services when component mounts
        const loadServices = async () => {
            try {
                const data = await getAllServices(branchId);
                setOptions(data);
            } catch (err) {
                console.error("Failed to load services", err);
                setError("Kunne ikke laste tjenester.");
            }
        };

        loadServices();
    }, [branchId]);

    const handlePhoneSubmit = async () => {
        if (!phoneNumber.trim()) {
            setError("Vennligst skriv inn telefonnummer.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await validatePhoneNumber(phoneNumber, branchId);
            setCustomer(response.customer);
            setStep(2);
        } catch (err) {
            console.error("Phone validation failed", err);
            setError("Ugyldig telefonnummer. Prøv igjen.");
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (option) => {
        setSelectedOptions((prevSelected) => {
            const isSelected = prevSelected.some((item) => {
                if (typeof option === "object" && option.service_name) {
                    return item.service_name === option.service_name;
                }
                return item === option;
            });

            if (isSelected) {
                return prevSelected.filter((item) => {
                    if (typeof option === "object" && option.service_name) {
                        return item.service_name !== option.service_name;
                    }
                    return item !== option;
                });
            } else {
                return [...prevSelected, option];
            }
        });
    };

    const handleCreateQueue = async () => {
        if (selectedOptions.length === 0) {
            setError("Vennligst velg minst én tjeneste.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const serviceIds = selectedOptions
                .map((option) => {
                    const id = typeof option === "object" ? option.id : option;
                    return id;
                })
                .join(",");

            // Get current time from UI to ensure consistency
            const currentUITime = new Date().toISOString();
            const response = await createSmsQueue(
                phoneNumber,
                branchId,
                serviceIds,
                currentUITime
            );

            setQueueNumber(response.queue_number);
            setSmsSent(response.sms_sent);

            // Generate SMS content for testing with proper tracking URL
            const branchName = "Test Branch"; // You can get this from response if available
            const trackingUrl = `${getFrontendUrl(
                `/home/Next_customer-view?queue_id=${
                    response.queue_id
                }&queue_number=${
                    response.queue_number
                }&branch_id=${branchId}&phone=${encodeURIComponent(
                    phoneNumber
                )}&service_ids=${serviceIds}`
            )}`;
            const smsText = `Din kønummer er ${response.queue_number} hos ${branchName}. Følg køen: ${trackingUrl}`;
            setSmsContent(smsText);
            setTrackingUrl(trackingUrl);

            setStep(3);

            // Store queue data in localStorage for potential future use
            localStorage.setItem(
                "smsQueueData",
                JSON.stringify({
                    phoneNumber,
                    queueNumber: response.queue_number,
                    customer: response.customer,
                    services: selectedOptions,
                    serviceIds: serviceIds,
                })
            );
        } catch (err) {
            console.error("Queue creation failed", err);
            setError("Kunne ikke opprette kø. Prøv igjen.");
        } finally {
            setLoading(false);
        }
    };

    const handleBackToPhone = () => {
        setStep(1);
        setCustomer(null);
        setSelectedOptions([]);
        setError(null);
    };

    const handleBackToServices = () => {
        setStep(2);
        setError(null);
    };

    const renderPhoneInput = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <Box mb={2}>
                <img
                    src={FMBlogo}
                    alt="FMB Logo"
                    style={{
                        width: isMobile ? 180 : 300,
                        maxWidth: "100%",
                    }}
                />
            </Box>

            <Typography
                variant="h5"
                color="primary"
                textAlign="center"
                mb={2}
                sx={{ fontWeight: "bold" }}
            >
                📱 SMS Kø
            </Typography>

            <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign="center"
                mb={3}
                sx={{ maxWidth: 500 }}
            >
                Skriv inn telefonnummeret ditt for å få et kønummer via SMS
            </Typography>

            <Box sx={{ width: "100%", maxWidth: 400, mb: 3 }}>
                <TextField
                    fullWidth
                    label="Telefonnummer"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+47 123 45 678"
                    variant="outlined"
                    error={!!error}
                    helperText={error}
                    sx={{ mb: 2 }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePhoneSubmit}
                    fullWidth
                    disabled={loading || !phoneNumber.trim()}
                >
                    {loading ? <CircularProgress size={24} /> : "Fortsett"}
                </Button>
            </Box>

            <Box mt={2}>
                <Button
                    variant="text"
                    onClick={() => navigate("/home/qr-display")}
                    sx={{ textTransform: "none" }}
                >
                    ← Tilbake til QR-kode
                </Button>
            </Box>

            <Box mt={4} textAlign="center">
                <Typography variant="body2">
                    <MuiLink href="#" underline="hover">
                        easyQue
                    </MuiLink>{" "}
                    (c) by Digifront
                </Typography>
            </Box>
        </Box>
    );

    const renderServiceSelection = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <Box mb={2}>
                <img
                    src={FMBlogo}
                    alt="FMB Logo"
                    style={{
                        width: isMobile ? 180 : 300,
                        maxWidth: "100%",
                    }}
                />
            </Box>

            <Typography
                variant="h5"
                color="primary"
                textAlign="center"
                mb={2}
                sx={{ fontWeight: "bold" }}
            >
                📱 Velg Tjenester
            </Typography>

            <Card sx={{ mb: 3, maxWidth: 400, width: "100%" }}>
                <CardContent>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Telefonnummer:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {phoneNumber}
                    </Typography>
                    {customer?.has_crm_profile && (
                        <Chip
                            label="CRM Profil Tilgjengelig"
                            color="success"
                            size="small"
                            sx={{ mt: 1 }}
                        />
                    )}
                </CardContent>
            </Card>

            <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign="center"
                mb={3}
                sx={{ maxWidth: 500 }}
            >
                Velg hvilke tjenester du trenger hjelp med. Du kan velge flere.
            </Typography>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2, maxWidth: 400, width: "100%" }}
                >
                    {error}
                </Alert>
            )}

            <Box sx={{ width: "100%", maxWidth: 400 }}>
                {options.map((option, index) => {
                    console.log("Rendering option:", option, "Index:", index);
                    return (
                        <Button
                            key={index}
                            variant={
                                selectedOptions.some((item) => {
                                    if (
                                        typeof option === "object" &&
                                        option.service_name
                                    ) {
                                        return (
                                            item.service_name ===
                                            option.service_name
                                        );
                                    }
                                    return item === option;
                                })
                                    ? "contained"
                                    : "outlined"
                            }
                            fullWidth
                            sx={{ mb: 1, textTransform: "none" }}
                            onClick={() => handleSelect(option)}
                        >
                            {option.service_name || option}
                        </Button>
                    );
                })}
            </Box>

            <Box
                sx={{
                    mt: 3,
                    display: "flex",
                    gap: 2,
                    width: "100%",
                    maxWidth: 400,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleBackToPhone}
                    sx={{ flex: 1 }}
                >
                    Tilbake
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateQueue}
                    sx={{ flex: 1 }}
                    disabled={selectedOptions.length === 0 || loading}
                >
                    {loading ? <CircularProgress size={24} /> : "Opprett Kø"}
                </Button>
            </Box>

            <Box mt={4} textAlign="center">
                <Typography variant="body2">
                    <MuiLink href="#" underline="hover">
                        easyQue
                    </MuiLink>{" "}
                    (c) by Digifront
                </Typography>
            </Box>
        </Box>
    );

    const renderSuccess = () => (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            px={2}
            sx={{ mt: { xs: 6, sm: 10 } }}
        >
            <Box mb={2}>
                <img
                    src={FMBlogo}
                    alt="FMB Logo"
                    style={{
                        width: isMobile ? 180 : 300,
                        maxWidth: "100%",
                    }}
                />
            </Box>

            <Typography
                variant="h4"
                color="primary"
                textAlign="center"
                mb={2}
                sx={{ fontWeight: "bold" }}
            >
                ✅ Kø Opprettet!
            </Typography>

            <Card sx={{ mb: 3, maxWidth: 400, width: "100%" }}>
                <CardContent textAlign="center">
                    <Typography
                        variant="h2"
                        color="primary"
                        fontWeight="bold"
                        mb={1}
                    >
                        #{queueNumber}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mb={2}>
                        Ditt kønummer
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sjekk SMS-en din og åpne lenken for å følge køen
                    </Typography>
                </CardContent>
            </Card>

            <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign="center"
                mb={3}
                sx={{ maxWidth: 500 }}
            >
                Takk for at du bruker vår SMS-kø! Sjekk SMS-en din og åpne
                lenken for å følge køen og vente på din tur.
            </Typography>

            <Box
                sx={{
                    mt: 3,
                    display: "flex",
                    gap: 2,
                    width: "100%",
                    maxWidth: 400,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleBackToServices}
                    sx={{ flex: 1 }}
                >
                    Endre Tjenester
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate("/home/customer-choice")}
                    sx={{ flex: 1 }}
                >
                    Ny Kø
                </Button>
            </Box>

            <Box mt={4} textAlign="center">
                <Typography variant="body2">
                    <MuiLink href="#" underline="hover">
                        easyQue
                    </MuiLink>{" "}
                    (c) by Digifront
                </Typography>
            </Box>
        </Box>
    );

    return (
        <>
            {step === 1 && renderPhoneInput()}
            {step === 2 && renderServiceSelection()}
            {step === 3 && renderSuccess()}
            <ScrollButtons />
        </>
    );
};

export default SmsSelectionPage;
